import { useState } from "react";
import { Copy, Check, Eye, EyeOff, AlertCircle, CheckCircle, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Client } from "@/types/client";
import { copyToClipboard } from "@/utils/clientUtils";
import { toast } from "sonner";
import { format } from "date-fns";

interface ViewCredentialsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  client: Client | null;
}

export function ViewCredentialsModal({ open, onOpenChange, client }: ViewCredentialsModalProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  if (!client) return null;

  const credentials = client.credentials;
  const loginUrl = `${window.location.origin}/login`;
  
  // Check if password was changed by client
  const changedByClient = (credentials as any)?.changed_by_client || false;
  const lastUpdated = (credentials as any)?.last_updated;

  const handleCopy = async (field: string, value: string) => {
    const success = await copyToClipboard(value);
    if (success) {
      setCopiedField(field);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopiedField(null), 2000);
    }
  };

  const handleCopyAll = async () => {
    if (credentials) {
      const allCreds = `Email: ${credentials.username}\nPassword: ${credentials.initialPassword || credentials.password || 'Password changed by client'}\nClient Code: ${credentials.clientCode}\nLogin URL: ${loginUrl}`;
      const success = await copyToClipboard(allCreds);
      if (success) {
        toast.success("All credentials copied!");
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-surface border-border">
        <DialogHeader>
          <DialogTitle>Client Credentials</DialogTitle>
          <DialogDescription>
            Login information for {client.name}
          </DialogDescription>
        </DialogHeader>

        <Alert className="bg-blue-500/10 border-blue-500/30">
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>
            Keep these credentials secure. Only share with authorized personnel.
          </AlertDescription>
        </Alert>

        <div className="space-y-3 py-4">
          {credentials ? (
            <>
              <CredentialRow
                label="Email / Username"
                value={credentials.username}
                copied={copiedField === 'username'}
                onCopy={() => handleCopy('username', credentials.username)}
              />
              <CredentialRow
                label="Password"
                value={showPassword ? credentials.initialPassword || credentials.password || 'Password changed by client' : '••••••••••••'}
                copied={copiedField === 'password'}
                onCopy={() => handleCopy('password', credentials.initialPassword || credentials.password || 'Password changed by client')}
                showToggle
                isVisible={showPassword}
                onToggleVisibility={() => setShowPassword(!showPassword)}
              />
              <CredentialRow
                label="Client Code"
                value={credentials.clientCode}
                copied={copiedField === 'clientCode'}
                onCopy={() => handleCopy('clientCode', credentials.clientCode)}
              />
              <CredentialRow
                label="Login URL"
                value={loginUrl}
                copied={copiedField === 'loginUrl'}
                onCopy={() => handleCopy('loginUrl', loginUrl)}
              />

              {/* Password Status Section */}
              <div className="mt-4 pt-4 border-t border-border space-y-3">
                <h4 className="text-sm font-medium text-muted-foreground">Password Status</h4>
                
                {/* Last Updated */}
                {lastUpdated && (
                  <div className="flex items-center gap-2 text-sm">
                    <Clock className="w-4 h-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Last Updated:</span>
                    <span>{format(new Date(lastUpdated), 'MMM d, yyyy h:mm a')}</span>
                  </div>
                )}

                {/* Changed By Client Status */}
                <div className={`flex items-center gap-2 p-3 rounded-lg ${
                  changedByClient 
                    ? 'bg-green-500/10 border border-green-500/30' 
                    : 'bg-amber-500/10 border border-amber-500/30'
                }`}>
                  {changedByClient ? (
                    <>
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span className="text-sm">Client has set their own password</span>
                    </>
                  ) : (
                    <>
                      <AlertCircle className="w-4 h-4 text-amber-500" />
                      <span className="text-sm">Using admin-generated password</span>
                    </>
                  )}
                </div>
              </div>
            </>
          ) : (
            <p className="text-center text-muted-foreground py-4">
              No credentials generated for this client yet.
            </p>
          )}
        </div>

        <div className="text-sm text-muted-foreground space-y-1">
          <p>Account Created: {format(new Date(client.joinedDate), 'MMM d, yyyy')}</p>
          <p>Last Contact: {format(new Date(client.lastContact), 'MMM d, yyyy')}</p>
        </div>

        <DialogFooter className="gap-2">
          {credentials && (
            <Button variant="ghost-border" onClick={handleCopyAll}>
              <Copy className="w-4 h-4 mr-2" />
              Copy All
            </Button>
          )}
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CredentialRow({ 
  label, 
  value, 
  copied, 
  onCopy,
  showToggle = false,
  isVisible = true,
  onToggleVisibility,
}: { 
  label: string; 
  value: string; 
  copied: boolean; 
  onCopy: () => void;
  showToggle?: boolean;
  isVisible?: boolean;
  onToggleVisibility?: () => void;
}) {
  return (
    <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30 border border-border">
      <div className="flex-1 min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="font-mono font-medium truncate">{value}</p>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        {showToggle && onToggleVisibility && (
          <Button variant="ghost" size="sm" onClick={onToggleVisibility}>
            {isVisible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </Button>
        )}
        <Button variant="ghost" size="sm" onClick={onCopy}>
          {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
        </Button>
      </div>
    </div>
  );
}
